import type { CloseState } from '../adapters/events.ts'
import type { AdapterFailureReason } from '../errors/index.ts'
import type { NashSkippedEntry } from '../nash/index.ts'
import type { CandidateId, LeadId, ManagerId, PipelineId, StatusId } from '../types/ids.ts'

/**
 * The closed vocabulary of `events.type` — the append-only log every fact about
 * a lead, a distribution round and a pipeline connection lands in.
 *
 * The vocabulary is **closed for writes and open for reads**, and the asymmetry
 * is deliberate. A producer can only write a member of {@link LudusEvent}; a
 * reader meets `type: string`, because the table holds rows written before this
 * union existed and a console bundle may be older than the engine that filled
 * it. Closing both ends would make a console deploy a prerequisite for an
 * engine deploy, which per-tenant deploys do not give us.
 */

/* ------------------------------------------------------------------- actor */

/**
 * Who caused a fact. Not a user id — authentication is still one shared bearer
 * token per tenant, so the engine cannot name a person. What it can always tell
 * apart is which *kind* of hand moved: someone driving the API, the
 * distributor, a source adapter mirroring its board, or a workflow acting on
 * its own.
 *
 * That distinction is the whole value, and it is why this column was cut from
 * the rename and arrives only now. While no facade mutation emitted, every row
 * would have carried one constant — provenance in appearance and nothing in
 * fact.
 *
 * `user:<id>` becomes expressible without a migration or a change of meaning
 * the day per-user auth lands. The grammar deliberately echoes `ludus-lead`'s
 * `LinkActor` rather than inventing a second one.
 */
export type EventActor =
  | 'system'
  | 'user'
  | `user:${string}`
  | `adapter:${string}`
  | `automation:${string}`

/** What the writer stamps when a caller names no actor. */
export const DEFAULT_EVENT_ACTOR: EventActor = 'system'

/* ------------------------------------------------------------------ family */

/**
 * Which axis of the system a fact belongs to. Closed set — a fourth family is
 * an ADR, never a config value.
 *
 * It is deliberately **not** a column. The family is the dotted prefix of
 * `type`, so storing it would denormalise a value that is already derivable,
 * and the only mechanism that would stop the two drifting — a SQLite `CHECK` —
 * cannot be added by `ALTER TABLE` at all, so it would force the destructive
 * table rebuild `events` refuses (see `packages/db/src/schema/events.ts`).
 *
 * `automation` is absent because nothing in the repo emits anything
 * automation-shaped yet. Adding it later is one line here and no migration,
 * which is exactly the property that makes leaving it out free.
 */
export type EventFamily = 'lead' | 'distribution' | 'pipeline'
export const EVENT_FAMILIES: readonly EventFamily[] = ['lead', 'distribution', 'pipeline'] as const

/**
 * Every event type is `<family>.<name>`. Not a naming convention — it is the
 * structure that lets a family be read off a type at all. {@link WellFamilied}
 * turns a member that breaks it into a compile error in this file rather than a
 * surprise at a call site.
 */
export type EventTypeName = `${EventFamily}.${string}`

type WellFamilied<E extends { type: EventTypeName }> = E

/* ---------------------------------------------------------- the vocabulary */

/**
 * The fields `lead.updated` may name. Closed, because the feed keys a label off
 * each one and a free string would put an unrenderable word in a human's
 * timeline.
 */
export type LeadEditableField = 'name' | 'price' | 'funnel' | 'tags'
export const LEAD_EDITABLE_FIELDS: readonly LeadEditableField[] = [
  'name',
  'price',
  'funnel',
  'tags',
] as const

interface LeadEditableValues {
  name: string
  price: number | null
  funnel: string | null
  tags: readonly string[]
}

const LEAD_FIELD_CHANGED: Record<
  LeadEditableField,
  (before: LeadEditableValues, after: LeadEditableValues) => boolean
> = {
  name: (b, a) => b.name !== a.name,
  price: (b, a) => b.price !== a.price,
  funnel: (b, a) => b.funnel !== a.funnel,
  tags: (b, a) => b.tags.length !== a.tags.length || b.tags.some((t, i) => t !== a.tags[i]),
}

export function changedLeadFields(
  before: LeadEditableValues,
  after: LeadEditableValues,
): LeadEditableField[] {
  return LEAD_EDITABLE_FIELDS.filter((field) => LEAD_FIELD_CHANGED[field](before, after))
}

/**
 * A lead's own history — the feed a human reads on the lead.
 *
 * Every member declares its fields. Before this union the log carried a
 * free-text `kind` beside a `payload: unknown`, and `lead.received` was written
 * in three mutually incompatible shapes (one of them with no `type` field at
 * all). The fields the old payloads smuggled in — `externalRef`, `closeState` —
 * are declared here instead.
 */
export type LeadEvent =
  /**
   * A lead entered this pipeline and was persisted with an internal status. An
   * item that left the source board and came back has entered again, which is
   * what this means — there is deliberately no separate revival type.
   */
  | {
      type: 'lead.received'
      leadId: LeadId
      pipelineId: PipelineId
      statusId: StatusId
      /** Mirrored leads only; a lead typed into Ludus has no source-side ref. */
      externalRef?: string | null
      closeState?: CloseState | null
      occurredAt: number
    }
  /**
   * The lead moved between internal statuses.
   *
   * There is no `lead.stage_changed`: "internal status" is the domain word, and
   * "moved to Paid" is this type plus a `toStatusId` predicate — a trigger, not
   * a vocabulary entry. `toStatusId` being a declared field rather than an
   * unknown payload key is what makes such a predicate typeable at all.
   */
  | {
      type: 'lead.status_changed'
      leadId: LeadId
      pipelineId: PipelineId
      fromStatusId: StatusId | null
      toStatusId: StatusId
      externalRef?: string | null
      closeState?: CloseState | null
      occurredAt: number
    }
  /**
   * A field changed that is neither a stage move nor a hand-over: name, price,
   * funnel, tags — the edits a person makes on the card.
   *
   * `changed` names the fields rather than carrying before/after values. The
   * feed needs to say *what* was touched, and a payload holding old values
   * would quietly become a second, partial copy of the lead's history that
   * nothing keeps in step. One edit that moves the stage AND renames the lead
   * emits `lead.status_changed` and `lead.updated` separately, so a reader
   * never has to unpick a compound.
   */
  | {
      type: 'lead.updated'
      leadId: LeadId
      pipelineId: PipelineId
      changed: readonly LeadEditableField[]
      occurredAt: number
    }
  /**
   * The distributor, a manual override or the source bound the lead to a
   * manager. `fromManagerId` is a reference like `fromStatusId`, not a copied
   * value, and is absent on rows written before it existed.
   */
  | {
      type: 'lead.assigned'
      leadId: LeadId
      pipelineId: PipelineId
      managerId: ManagerId
      fromManagerId?: ManagerId | null
      occurredAt: number
    }
  | {
      type: 'lead.unassigned'
      leadId: LeadId
      pipelineId: PipelineId
      fromManagerId: ManagerId
      occurredAt: number
    }
  /** A free-text note was attached to the lead from the CRM side. */
  | {
      type: 'lead.note_added'
      leadId: LeadId
      pipelineId: PipelineId
      body: string
      occurredAt: number
    }
  /**
   * A flow decided this card owes the customer a mail, and wrote it down (#377).
   *
   * The three mail types are `lead.*` rather than a family of their own because
   * a mail to the customer is something that happened *to the sale* — the thing
   * a manager reading the timeline needs to know before they phone. It is not
   * the engine explaining itself, which is what `distribution.*` is for.
   *
   * **Scheduled is not sent, and the vocabulary says so in three words rather
   * than one.** Nothing here can promise delivery: the mail leaves from a
   * process this one only talks to, so the honest log is a decision, then an
   * outcome. `mailId` is what ties them together — minted here, carried through
   * every hop, and the key the callback arrives on.
   */
  | {
      type: 'lead.email_scheduled'
      leadId: LeadId
      pipelineId: PipelineId
      mailId: string
      /** The sink's own template name, recorded verbatim and never interpreted. */
      template: string
      /** The recipient as resolved at schedule time, not as configured. */
      to: string
      lang: string | null
      occurredAt: number
    }
  | {
      type: 'lead.email_sent'
      leadId: LeadId
      pipelineId: PipelineId
      mailId: string
      template: string
      to: string
      occurredAt: number
    }
  /**
   * The sink tried and could not.
   *
   * A separate type rather than `lead.email_sent` with a flag, because the two
   * are read by different people for different reasons and a timeline that
   * renders them alike buries the one that needs acting on.
   */
  | {
      type: 'lead.email_failed'
      leadId: LeadId
      pipelineId: PipelineId
      mailId: string
      template: string
      to: string
      /** What the sink reported. Never a stack, never a credential. */
      error: string
      occurredAt: number
    }

/**
 * What one Nash round decided, or refused to decide. Never a lead fact — these
 * are the engine explaining itself, and they are what `family` exists to keep
 * out of a manager's timeline.
 *
 * The round's scoreboard is not here: it stays a structured row in
 * `nash_decisions`, and the event only names the decision.
 */
export type DistributionEvent =
  | {
      type: 'distribution.round_completed'
      leadId: LeadId
      pipelineId: PipelineId
      selectedCandidateId: CandidateId
      occurredAt: number
    }
  | {
      type: 'distribution.no_candidates'
      leadId: LeadId
      pipelineId: PipelineId
      skipped: readonly NashSkippedEntry[]
      occurredAt: number
    }
  /** The trigger named a status this pipeline has never had — config mismatch. */
  | {
      type: 'distribution.unknown_status'
      leadId: LeadId
      pipelineId: PipelineId
      statusId: StatusId
      occurredAt: number
    }
  | {
      type: 'distribution.assignment_unroutable'
      leadId: LeadId
      pipelineId: PipelineId
      managerId: ManagerId
      reason: 'manager_has_no_identity_for_source'
      occurredAt: number
    }

/** The pipeline's connection to its source adapter. May or may not name a lead. */
export type PipelineEvent =
  /**
   * The adapter answered 501. `leadId` and `externalRef` are both optional
   * because ingest fails before a lead row exists while `writeAssignment` fails
   * after one does.
   */
  | {
      type: 'pipeline.capability_gap'
      pipelineId: PipelineId
      method: string
      adapterKind: string | null
      leadId?: LeadId
      externalRef?: string
      occurredAt: number
    }
  /**
   * An ADR-0001 §3 transport failure.
   *
   * The type is *derived* from {@link AdapterFailureReason} rather than
   * mirroring it as four hand-written literals. That union is simultaneously
   * `AdapterProxyError`'s discriminator, the input to its `retryable`, and the
   * string an operator reads on a 502 — so it must not be forked. A fifth
   * reason added there becomes a missing key in `EVENT_TYPE_COVERAGE` below,
   * which is a compile error here. That is the drift guard.
   */
  | {
      type: `pipeline.${AdapterFailureReason}`
      pipelineId: PipelineId
      method: string
      adapterKind: string
      status: number | null
      message: string
      occurredAt: number
    }
  | {
      type: 'pipeline.provisioning_hint_rejected'
      pipelineId: PipelineId
      clamped: readonly string[]
      invalid: readonly string[]
      occurredAt: number
    }

/** The closed vocabulary of `events.type`, as objects rather than as strings. */
export type LudusEvent = WellFamilied<LeadEvent | DistributionEvent | PipelineEvent>
export type LudusEventType = LudusEvent['type']

/**
 * Canonical internal event vocabulary the engine consumes. Adapters translate
 * `ExternalLeadEvent` into zero or more of these; nothing downstream of the
 * adapter ever sees CRM-shaped data.
 *
 * It is now a *slice* of the spine rather than a parallel vocabulary, so the
 * two cannot drift apart the way they had.
 */
export type LudusInternalEvent = Extract<LudusEvent, { type: `lead.${string}` }>

/**
 * The runtime list, closed at COMPILE time in both directions: a union member
 * with no key here is an error, and a key naming no member is an error. That is
 * strictly stronger than a list plus a pinning test, because there is no test
 * to drift.
 */
const EVENT_TYPE_COVERAGE: Record<LudusEventType, true> = {
  'lead.received': true,
  'lead.status_changed': true,
  'lead.updated': true,
  'lead.assigned': true,
  'lead.unassigned': true,
  'lead.note_added': true,
  'lead.email_scheduled': true,
  'lead.email_sent': true,
  'lead.email_failed': true,
  'distribution.round_completed': true,
  'distribution.no_candidates': true,
  'distribution.unknown_status': true,
  'distribution.assignment_unroutable': true,
  'pipeline.capability_gap': true,
  'pipeline.provisioning_hint_rejected': true,
  'pipeline.adapter_timeout': true,
  'pipeline.adapter_error': true,
  'pipeline.adapter_protocol_error': true,
  'pipeline.adapter_unreachable': true,
}

export const EVENT_TYPES: readonly LudusEventType[] = Object.keys(
  EVENT_TYPE_COVERAGE,
) as LudusEventType[]

/** The one place an ADR-0001 §3 reason becomes an event type. */
export const adapterFailureEventType = (
  reason: AdapterFailureReason,
): `pipeline.${AdapterFailureReason}` => `pipeline.${reason}`

/* ------------------------------------------------------ write side (closed) */

/**
 * What a producer hands the writer.
 *
 * `pipelineId` and `occurredAt` are stripped because the writer owns both: a
 * producer that could stamp its own time is a producer that can disagree with
 * the log's order, and a producer that could name its own pipeline can write a
 * row onto someone else's board. `Omit` is distributed over the union so the
 * discriminant survives — a literal still narrows, and a surplus property is
 * still rejected.
 */
export type EventDraft<E extends LudusEvent = LudusEvent> = E extends unknown
  ? Omit<E, 'pipelineId' | 'occurredAt'>
  : never

/** Total function: the row's `lead_id` is read off the event, never passed. */
export function leadIdOf(event: LudusEvent): LeadId | null {
  return 'leadId' in event && event.leadId !== undefined ? event.leadId : null
}

/**
 * Where a fact sits in the causal chain (ADR-0006 §6).
 *
 * A node that creates a lead emits an event the same bus reads, so
 * Delivery → Sales → Delivery is one careless flow away. This is what makes
 * that detectable: `correlationId` names the root of the chain, `causationId`
 * the event that caused this one, and `causeDepth` how many hops from the
 * original human or source action.
 *
 * Depth is carried rather than derived. Walking the chain to compute it would
 * be a read per hop on the drain's hot path, which is the one path that must
 * stay cheap.
 *
 * It travels as a **construction dependency of the writer**, beside `actor` —
 * never as a field a producer fills in. A producer that can name its own
 * provenance can lie about it, and the guard is only worth having while it
 * cannot be talked out of.
 */
export interface EventCause {
  correlationId: string
  causationId: string
  causeDepth: number
}

/* -------------------------------------------------------- read side (OPEN) */

/**
 * A stored row as a reader meets it.
 *
 * `type` is `string`, not {@link LudusEventType}, and that is the open half of
 * the asymmetry this module opens with. A row written before the vocabulary
 * existed, or by an engine newer than the bundle reading it, must render
 * through a fallback rather than crash an exhaustive switch. Consumers key a
 * `Partial<Record<LudusEventType, …>>` and default; they never switch on `type`
 * without one.
 *
 * `payload` is `unknown` for the same reason, and more sharply: historical rows
 * carry payloads that do not match the member their `type` now names.
 */
export interface EventRecord {
  id: string
  pipelineId: PipelineId
  leadId: LeadId | null
  type: string
  /**
   * `null` on rows written before the column existed. A reader shows "the
   * system" or nothing at all for those — it must not backfill a guess, which
   * would be inventing provenance.
   */
  actor: string | null
  /** Epoch millis on the wire, matching `nash_decisions`' posture. */
  occurredAt: number
  payload: unknown
  /**
   * The bus position (ADR-0006 §1), or `null` on a row a reader met before the
   * column was materialised.
   *
   * Open like `type` and `payload`, and for the same reason: this is the read
   * side, and a row is not wrong for predating a column. A consumer that needs
   * the ordering must handle the `null` rather than assume the backfill has
   * reached every deployment it will ever read.
   */
  seq: number | null
  /**
   * The causal chain as stored — `null` on every row written before the
   * facade began stamping it, and on any fact that started a chain rather than
   * continued one. Never backfilled with a guess: an invented provenance is
   * worse than an absent one, because it reads as evidence.
   */
  correlationId: string | null
  causationId: string | null
  causeDepth: number | null
}

/** Narrow a stored row into the closed union when this build does know the type. */
export function isKnownEventType(type: string): type is LudusEventType {
  return (EVENT_TYPES as readonly string[]).includes(type)
}
